import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { List, Plus, Check, LinkSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { queryKeys } from '../queries/keys';
import { fetchItemDetail } from '../queries/items';
import Sidebar from '../components/Sidebar';
import ItemCard from '../components/ItemCard';
import ItemDetailModal from '../components/ItemDetailModal';
import UploadModal from '../components/UploadModal';
import SaveUrlModal from '../components/SaveUrlModal';
import { isAnyModalOpen } from '../components/Modal';
import SearchBox from '../components/SearchBox';
import EmptyState from '../components/EmptyState';
import Toast from '../components/Toast';
import SearchSummary from '../components/SearchSummary';
import { useProcessingStatus } from '../hooks/useProcessingStatus';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useUrlModalParam } from '../hooks/useUrlModalParam';
import { useSearch } from '../hooks/useSearch';
import { getItemCategory, getItemHeadline } from '../utils/itemHelpers';
import { MAX_UPLOAD_IMAGE_BYTES } from '../utils/uploadLimits';
import SelectionBar from '../components/SelectionBar';
import CollectionPickerModal from '../components/CollectionPickerModal';
import { CardGridSkeleton } from '../components/Skeleton';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'alpha', label: 'Title (A-Z)' },
  { value: 'category-asc', label: 'Category (A-Z)' },
  { value: 'category-desc', label: 'Category (Z-A)' },
];

// Page size for both the initial load and subsequent "load more" pages
const PAGE_SIZE = 30;

// Deduplicate items by primary ID (prevents carousel items appearing multiple times)
function deduplicateItems(items) {
  const seen = new Set();
  return items.filter(item => {
    const id = item.item_id || item.id;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export default function Gallery() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = user?.isAdmin || false;

  // Filter/search/modal state lives in the URL (COL-87), not component
  // state, so a fresh mount from a pasted link — or a browser back/forward —
  // reproduces exactly the same view. `searchParams` is the single source of
  // truth; everything below derives from it rather than shadowing it into
  // useState (a shadow copy is exactly what let COL-88's query keys drift
  // from what the URL could actually reproduce). The item modal's own
  // open/replace/close/no-op-on-reopen rules live in useUrlModalParam,
  // shared with Entities.jsx's identical `?enrichment=` modal.
  const {
    selectedId: selectedItemId,
    open: openItemParam,
    close: handleCloseModal,
    searchParams,
    setSearchParams,
    setSearchParamsRef,
  } = useUrlModalParam('item');
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedSubcategory = searchParams.get('subcategory') || null;
  const searchQuery = searchParams.get('q') || '';

  useScrollRestoration();

  const [userFilter, setUserFilter] = useState('all'); // 'all' or a user_id
  const [sortBy, setSortBy] = useState('newest');
  const [searchBoxResetKey, setSearchBoxResetKey] = useState(0);
  // Search core (mutation + searching flag + isSubmit -> includeAnswer
  // mapping) is shared with Places/Entities via useSearch (COL-138).
  // clearStaleAnswerOnSearch: true is Gallery-specific — it nulls the AI
  // answer immediately on a new search while leaving previous items in
  // place, which is what makes the stale-while-refetch dim treatment below
  // (`searching` on the grid) show old items instead of an old answer.
  // Declared here (rather than next to handleSearch below) because
  // loadMoreItems/the infinite-scroll observer below need `searchResults`
  // in their dependency arrays before handleSearch is otherwise needed.
  const {
    searchResults,
    setSearchResults,
    searching,
    search,
    searchMutation,
  } = useSearch({
    scope: 'items',
    categoryFilter: selectedCategory === 'all' ? undefined : selectedCategory,
    clearStaleAnswerOnSearch: true,
    onError: () => showToast('Search failed', 'error'),
  });
  // Full item object backing the open modal — resolved from selectedItemId
  // below (grid data optimistically, then full detail in the background).
  const [selectedItem, setSelectedItem] = useState(null);
  // Track which item ID is currently loading (null = not loading, string = loading that item)
  const [loadingItemId, setLoadingItemId] = useState(null);
  // Track which item ID is being deleted (prevents double-click)
  const [deletingItemId, setDeletingItemId] = useState(null);
  // Custom confirm dialog state (replaces window.confirm to avoid browser suppression)
  const [confirmDelete, setConfirmDelete] = useState(null); // stores itemId to delete
  const [showUpload, setShowUpload] = useState(false);
  const [showSaveUrl, setShowSaveUrl] = useState(false);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Multiselect mode for adding to collections
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  // Track items currently being processed: { itemId: jobId }
  // Persisted to sessionStorage to survive page refreshes
  const [processingJobs, setProcessingJobs] = useState(() => {
    try {
      const stored = sessionStorage.getItem('processingJobs');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Persist processingJobs to sessionStorage
  useEffect(() => {
    try {
      if (Object.keys(processingJobs).length > 0) {
        sessionStorage.setItem('processingJobs', JSON.stringify(processingJobs));
      } else {
        sessionStorage.removeItem('processingJobs');
      }
    } catch {
      // Ignore storage errors
    }
  }, [processingJobs]);

  // Items whose ItemCard should play the one-time "analysis reveal" (COL-92)
  // on its next mount — set the instant a processing job completes, and
  // normally cleared by ItemCard itself (via onRevealed) once it has
  // consumed the flag. Deliberately NOT persisted to sessionStorage like
  // processingJobs: it's a fire-once cue for the mount that happens right
  // after completion, not state that needs to survive a reload.
  const [justCompletedIds, setJustCompletedIds] = useState(() => new Set());
  // Fallback expiry for the above: an item can finish processing while its
  // ItemCard isn't actually rendered (an active search, or an admin
  // userFilter hiding it), so onRevealed never fires to clear the flag. Caps
  // how long a stale flag can survive so a much-later, unrelated mount (the
  // user clears the search minutes on) doesn't replay the reveal as if the
  // item had just finished.
  const justCompletedTimeoutsRef = React.useRef({});
  useEffect(() => {
    const timeouts = justCompletedTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);

  const clearJustCompleted = useCallback((itemId) => {
    clearTimeout(justCompletedTimeoutsRef.current[itemId]);
    delete justCompletedTimeoutsRef.current[itemId];
    setJustCompletedIds(prev => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  // Toast helper with ref for cleanup
  const toastTimeoutRef = React.useRef(null);
  const showToast = useCallback((message, type = 'info') => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const itemsKey = queryKeys.items(user?.id, {
    category: selectedCategory === 'all' ? undefined : selectedCategory,
    subcategory: selectedSubcategory || undefined,
  });

  // Fetch categories from API. Cache-first — instant on revisit.
  // enabled: !!user?.id is a second, query-level guard against ever firing
  // under the shared 'anon' key namespace (see queries/keys.js) — belt and
  // braces alongside ProtectedRoute never mounting this page pre-auth.
  const { data: categoriesData } = useQuery({
    queryKey: queryKeys.categories(user?.id),
    queryFn: () => api.getCategories(),
    enabled: !!user?.id,
  });

  // Fetch items (offset pagination via useInfiniteQuery). A category/
  // subcategory change is a different query key, so it gets its own cache
  // entry and loads fresh; switching back to a previously-viewed
  // category/page renders instantly from cache.
  const {
    data: itemsPages,
    isLoading: loading,
    isFetchingNextPage: loadingMore,
    hasNextPage: hasMore,
    fetchNextPage,
    isError: itemsError,
  } = useInfiniteQuery({
    queryKey: itemsKey,
    queryFn: ({ pageParam }) => api.getItems({
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      subcategory: selectedSubcategory || undefined,
      limit: PAGE_SIZE,
      offset: pageParam,
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: !!user?.id,
  });
  const items = React.useMemo(
    () => itemsPages?.pages.flatMap(page => page.items) ?? [],
    [itemsPages]
  );
  const totalItems = itemsPages?.pages[itemsPages.pages.length - 1]?.total ?? 0;

  useEffect(() => {
    if (itemsError) showToast('Failed to load items', 'error');
  }, [itemsError, showToast]);

  // Load more items (pagination)
  const loadMoreItems = useCallback(() => {
    if (loadingMore || !hasMore || searchResults) return;
    fetchNextPage();
  }, [loadingMore, hasMore, searchResults, fetchNextPage]);

  // Infinite scroll: observe sentinel element to trigger load more
  const loadMoreRef = React.useRef(null);
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && !searchResults) {
          loadMoreItems();
        }
      },
      { rootMargin: '200px' } // Start loading before reaching the end
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, searchResults, loadMoreItems]);

  const handleSearch = useCallback((query, opts = {}) => {
    const trimmed = query.trim();
    // `q` is only for restorability/shareability (search itself is a
    // one-off mutation, not a cached query — see useSearch) — replace, so
    // typing doesn't spam history the way opening an item modal does.
    setSearchParams((prev) => {
      if (trimmed) prev.set('q', query); else prev.delete('q');
      return prev;
    }, { replace: true });
    search(query, opts);
  }, [search, setSearchParams]);

  // Deep-link restore: a `?q=` present on mount (pasted URL, refresh) runs
  // the search exactly once, as soon as auth has resolved. Treated as an
  // explicit submit (includeAnswer: true) — the user navigated straight to
  // a fully-formed query URL, not mid-keystroke, so a "new tab, same view"
  // deep link restores the AI answer too if one was showing when the URL
  // was copied. Later searches all go through handleSearch above. Calls the
  // raw searchMutation directly (not handleSearch/search) since the URL's
  // `q` is trusted as-is here, not re-validated by the trim-check `search`
  // applies.
  const initialSearchRanRef = React.useRef(false);
  useEffect(() => {
    if (initialSearchRanRef.current || !user?.id) return;
    initialSearchRanRef.current = true;
    const q = searchParams.get('q');
    if (q) searchMutation.mutate({ query: q, includeAnswer: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Unique user IDs for admin filter dropdown
  const uniqueUsers = React.useMemo(() => {
    if (!isAdmin) return [];
    const users = new Set();
    items.forEach(item => {
      if (item.user_id) users.add(item.user_id);
    });
    return Array.from(users).sort();
  }, [items, isAdmin]);

  // Sort items (and apply admin user filter), deduplicate by item ID
  const sortedItems = React.useMemo(() => {
    // For search results, preserve relevance order from API
    if (searchResults?.items) {
      const results = deduplicateItems(searchResults.items);
      if (isAdmin && userFilter !== 'all') {
        return results.filter(item => item.user_id === userFilter);
      }
      return results;
    }

    // Apply deduplication and user filter for admin
    let filtered = deduplicateItems(items);
    if (isAdmin && userFilter !== 'all') {
      filtered = filtered.filter(item => item.user_id === userFilter);
    }

    // Helper: compare categories with explicit null handling
    const compareCategories = (catA, catB, ascending = true) => {
      const hasA = Boolean(catA);
      const hasB = Boolean(catB);
      // Uncategorized items go to end for ascending, beginning for descending
      if (!hasA && !hasB) return 0;
      if (!hasA) return ascending ? 1 : -1;
      if (!hasB) return ascending ? -1 : 1;
      const result = catA.toLowerCase().localeCompare(catB.toLowerCase());
      return ascending ? result : -result;
    };

    // For regular items, apply sorting
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'alpha':
          return getItemHeadline(a).localeCompare(getItemHeadline(b));
        case 'category-asc': {
          // Sort by category A-Z, then by date newest first
          const catCompare = compareCategories(getItemCategory(a), getItemCategory(b), true);
          if (catCompare !== 0) return catCompare;
          return new Date(b.created_at) - new Date(a.created_at);
        }
        case 'category-desc': {
          // Sort by category Z-A, then by date newest first
          const catCompare = compareCategories(getItemCategory(a), getItemCategory(b), false);
          if (catCompare !== 0) return catCompare;
          return new Date(b.created_at) - new Date(a.created_at);
        }
        default: // newest
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
  }, [items, searchResults, sortBy, isAdmin, userFilter]);

  // Categories with counts from API
  const categories = React.useMemo(() => {
    if (!categoriesData) {
      return [{ id: 'all', name: 'All', count: items.length }];
    }

    const result = [
      { id: 'all', name: 'All', count: categoriesData.total_items },
    ];

    // Add categories from API
    categoriesData.categories.forEach(cat => {
      result.push({
        id: cat.name,
        name: cat.name,
        count: cat.item_count,
        subcategories: categoriesData.subcategories
          .filter(sub => sub.category === cat.name)
          .map(sub => ({
            id: sub.name,
            name: sub.name,
            count: sub.item_count,
          })),
      });
    });

    // Add uncategorized if any
    if (categoriesData.uncategorized_count > 0) {
      result.push({
        id: '__uncategorized__',
        name: 'Uncategorized',
        count: categoriesData.uncategorized_count,
      });
    }

    return result;
  }, [categoriesData, items.length]);

  // Helper to remove a processing job (used by both complete and failed handlers)
  const handleExitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedItemIds(new Set());
  }, []);

  const handleCollectionPickerSuccess = useCallback((message) => {
    showToast(message, 'success');
    handleExitSelectMode();
  }, [showToast, handleExitSelectMode]);

  const removeProcessingJob = useCallback((itemId) => {
    setProcessingJobs(prev => {
      const { [itemId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Handle processing job completion
  const handleProcessingComplete = useCallback(async (itemId) => {
    removeProcessingJob(itemId);
    // Flag this item so the ItemCard that mounts in place of the processing
    // card plays the analysis reveal exactly once (COL-92). The timeout is
    // the fallback expiry described above `justCompletedIds` — a minute is
    // comfortably longer than the mount+notify round trip for the normal
    // case (a single render, effectively instant) while still bounding how
    // long a flag can survive if the card never mounts to consume it.
    setJustCompletedIds(prev => new Set(prev).add(itemId));
    clearTimeout(justCompletedTimeoutsRef.current[itemId]);
    justCompletedTimeoutsRef.current[itemId] = setTimeout(() => clearJustCompleted(itemId), 60_000);

    // Fetch updated item with analysis data and patch it into the cached
    // items list in place — no need to refetch the whole page just because
    // one item finished processing.
    try {
      const updatedItem = await fetchItemDetail(queryClient, user?.id, itemId);
      queryClient.setQueryData(itemsKey, (old) => old ? {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          items: page.items.map(item =>
            (item.id === itemId || item.item_id === itemId) ? updatedItem : item
          ),
        })),
      } : old);
      showToast('Processing complete!', 'success');
    } catch {
      // Item will be refreshed on next fetch
    }

    // Refresh categories in case they changed
    queryClient.invalidateQueries({ queryKey: queryKeys.categories(user?.id) });
  }, [removeProcessingJob, clearJustCompleted, queryClient, itemsKey, user?.id, showToast]);

  // Handle processing job failure
  const handleProcessingFailed = useCallback((itemId) => {
    removeProcessingJob(itemId);
    showToast('Processing failed. The item may be incomplete.', 'error');
  }, [removeProcessingJob, showToast]);

  // Add one or more newly-uploaded items to the cached list in a single
  // cache write (and start tracking their processing jobs in a single state
  // update), rather than one write per item — a multi-file paste otherwise
  // triggers N full page-list traversals and N re-renders instead of one.
  // No toast here, so callers that upload several files at once (paste) can
  // show one summary toast instead of one per file. `total` is bumped on
  // every page (each page's `total` mirrors the same overall count from the
  // API), so the "Load More (X of Y)" label stays correct even when more
  // than one page is already loaded.
  const addUploadedItems = useCallback((uploaded) => {
    if (uploaded.length === 0) return;

    queryClient.setQueryData(itemsKey, (old) => {
      if (!old) return old;
      const newItems = uploaded.map((u) => u.item);
      const pages = old.pages.map((page, i) => ({
        ...page,
        items: i === 0 ? [...newItems, ...page.items] : page.items,
        total: (page.total ?? 0) + newItems.length,
      }));
      return { ...old, pages };
    });

    const newJobs = Object.fromEntries(uploaded.filter((u) => u.jobId).map((u) => [u.item.id, u.jobId]));
    if (Object.keys(newJobs).length > 0) {
      setProcessingJobs(prev => ({ ...prev, ...newJobs }));
    }
  }, [queryClient, itemsKey]);

  // Handle upload complete. Processing (analysis, enrichment, embedding) is
  // triggered automatically by the backend.
  const handleUploadComplete = useCallback((newItem, jobId) => {
    setShowUpload(false);
    showToast('Screenshot uploaded! Processing started.', 'success');
    addUploadedItems([{ item: newItem, jobId }]);
  }, [addUploadedItems, showToast]);

  // Handle URL-save completion (COL-137). The collector job only ever gives
  // us an item_id — look up the full item and its ProcessingJob (a distinct
  // id space from CollectorJob), then hand off to the same processingJobs
  // state / ProcessingItemCard machinery already used for uploads, instead
  // of a bespoke second progress UI.
  const handleCollected = useCallback(async (itemId) => {
    setShowSaveUrl(false);
    try {
      const [item, statusResponse] = await Promise.all([
        fetchItemDetail(queryClient, user?.id, itemId),
        api.getProcessingStatusForItem(itemId),
      ]);
      const jobId = statusResponse.jobs?.[0]?.job_id || null;
      showToast('URL saved! Processing started.', 'success');
      addUploadedItems([{ item, jobId }]);
    } catch (err) {
      // The collector job itself succeeded (item_id exists) — a failure here
      // is only our own follow-up lookup. Surface it rather than silently
      // dropping the item: the user can find it via a normal refresh/search.
      showToast(`Saved, but couldn't load it yet: ${err.message}`, 'error');
    }
  }, [queryClient, user?.id, addUploadedItems, showToast]);

  // Handle the already-collected short-circuit: this exact URL was already
  // saved by this user (server-side check in POST /collect) — surface the
  // existing item via the normal item-detail modal instead of erroring or
  // creating a duplicate.
  const handleAlreadyCollected = useCallback((itemId) => {
    setShowSaveUrl(false);
    showToast('You already saved this URL.', 'info');
    openItemParam(itemId);
  }, [openItemParam, showToast]);

  // Paste-to-upload (COL-96): pasting an image from the clipboard anywhere on
  // the Gallery starts an upload through the same path as the Upload modal,
  // without needing to open it first. Uploads run in parallel (independent
  // S3 presign+PUT+fetch calls, no reason to serialize them), and report a
  // single summary toast — one toast per file would let a later file's
  // "uploading" status silently overwrite an earlier file's error before the
  // user ever sees it.
  const handlePastedFiles = useCallback(async (files) => {
    const oversized = files.filter((file) => file.size > MAX_UPLOAD_IMAGE_BYTES);
    const toUpload = files.filter((file) => file.size <= MAX_UPLOAD_IMAGE_BYTES);

    const settled = await Promise.allSettled(toUpload.map((file) => api.uploadFile(file)));
    const succeeded = settled.filter((r) => r.status === 'fulfilled');
    const failed = settled.filter((r) => r.status === 'rejected');
    addUploadedItems(succeeded.map((r) => ({ item: r.value.item, jobId: r.value.jobId })));

    // The single-success case keeps the exact wording used for the Upload
    // modal's own success toast, since that's by far the common case;
    // everything else (any failure, any oversized rejection, or more than
    // one file) gets one summary toast covering every outcome — never one
    // toast per file, which could silently overwrite an earlier result.
    if (files.length === 1 && succeeded.length === 1) {
      showToast('Screenshot uploaded! Processing started.', 'success');
      return;
    }

    const parts = [];
    if (succeeded.length > 0) parts.push(`${succeeded.length} image${succeeded.length === 1 ? '' : 's'} uploaded`);
    if (failed.length > 0) parts.push(`${failed.length} failed to upload${failed.length === 1 ? `: ${failed[0].reason?.message || 'unknown error'}` : ''}`);
    if (oversized.length > 0) parts.push(`${oversized.length} too large to paste (max 20MB)`);
    const allSucceeded = succeeded.length === files.length;
    showToast(parts.join(', '), allSucceeded ? 'success' : succeeded.length > 0 ? 'info' : 'error');
  }, [addUploadedItems, showToast]);

  useEffect(() => {
    const handlePaste = (e) => {
      // Never act while a modal is open — a global listener with no
      // awareness of e.g. an open UploadModal would otherwise silently
      // discard a staged-but-not-yet-submitted file there, or start an
      // unrelated upload while the user is just viewing an ItemDetailModal.
      if (isAnyModalOpen()) return;

      // Never hijack a normal paste into a text field — search box,
      // collection name input, category autocomplete, anything editable
      // anywhere on the page. Checking the currently-focused element (not
      // just the event target) covers every case: a field always has focus
      // while the user is pasting into it.
      const active = document.activeElement;
      const isEditableTarget = active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable
      );
      if (isEditableTarget) return;

      const clipboardItems = Array.from(e.clipboardData?.items || []);
      // A paste can contain multiple items (e.g. an image + its alt text, or
      // several copied images) — collect every image file, ignore the rest
      // (plain text/HTML paste is simply not our concern outside a field).
      const imageFiles = clipboardItems
        .filter((clipboardItem) => clipboardItem.kind === 'file' && clipboardItem.type.startsWith('image/'))
        .map((clipboardItem) => clipboardItem.getAsFile())
        .filter(Boolean);

      if (imageFiles.length === 0) return; // no images — leave the paste alone entirely

      e.preventDefault();
      handlePastedFiles(imageFiles);
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePastedFiles]);

  // Index of the item backing the open modal, derived from the URL rather
  // than tracked separately — recomputed whenever the list or the URL
  // changes, so it can never drift from what `?item=` actually points at.
  const selectedItemIndex = selectedItemId
    ? sortedItems.findIndex(item => (item.item_id || item.id) === selectedItemId)
    : -1;

  // Open (or switch to) an item's detail modal — history push/replace/no-op
  // rules live in useUrlModalParam (shared with Entities.jsx's enrichment
  // modal); this just adds the optimistic grid-data paint on top, skipped
  // when openItemParam no-ops because the item shown is already this one.
  const openItem = useCallback((item) => {
    const itemId = item.item_id || item.id;
    if (!openItemParam(itemId)) return;
    setSelectedItem(item);
  }, [openItemParam]);

  const handleItemClick = useCallback((item) => {
    // In select mode, toggle selection instead of opening detail
    if (isSelectMode) {
      const itemId = item.item_id || item.id;
      setSelectedItemIds(prev => {
        const next = new Set(prev);
        if (next.has(itemId)) {
          next.delete(itemId);
        } else {
          next.add(itemId);
        }
        return next;
      });
      return;
    }
    openItem(item);
  }, [isSelectMode, openItem]);

  // Resolve `selectedItem` from the URL: fetch full detail in the
  // background (cache-first via fetchItemDetail — instant if already
  // fetched elsewhere), sharing the cache with processing completion,
  // Places' related-items modal, and collection detail. An item that
  // doesn't exist or belongs to another user 404s identically server-side
  // (get_by_id filters by user_id — see lambdas/CLAUDE.md), so this can't
  // distinguish "missing" from "not yours": it just degrades gracefully
  // either way — toast, strip the param, no crash, no infinite spinner.
  useEffect(() => {
    if (!selectedItemId) {
      setSelectedItem(null);
      setLoadingItemId(null);
      return;
    }
    if (!user?.id) return;

    let cancelled = false;
    setLoadingItemId(selectedItemId);
    fetchItemDetail(queryClient, user.id, selectedItemId)
      .then((fullItem) => {
        if (cancelled) return;
        setSelectedItem(fullItem);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load item from URL:', err);
        showToast('That item is unavailable', 'error');
        setSelectedItem(null);
        setSearchParamsRef.current((prev) => {
          prev.delete('item');
          return prev;
        }, { replace: true });
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingItemId((current) => current === selectedItemId ? null : current);
        }
      });

    return () => { cancelled = true; };
    // setSearchParams intentionally excluded — see setSearchParamsRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, user?.id, queryClient, showToast]);

  // Prev/Next navigation in modal
  const handlePrevItem = useCallback(() => {
    if (selectedItemIndex > 0) {
      openItem(sortedItems[selectedItemIndex - 1]);
    }
  }, [selectedItemIndex, sortedItems, openItem]);

  const handleNextItem = useCallback(() => {
    if (selectedItemIndex < sortedItems.length - 1) {
      openItem(sortedItems[selectedItemIndex + 1]);
      // Prefetch more items if near end of visible list
      if (selectedItemIndex + 3 >= sortedItems.length && hasMore && !loadingMore) {
        loadMoreItems();
      }
    }
  }, [selectedItemIndex, sortedItems, openItem, hasMore, loadingMore, loadMoreItems]);

  // Handle delete - show confirmation dialog first
  const handleDeleteRequest = useCallback((itemId) => {
    if (deletingItemId) return; // Prevent double-click
    setConfirmDelete(itemId);
  }, [deletingItemId]);

  // Execute delete after user confirms
  const handleDeleteConfirm = useCallback(async (itemId) => {
    if (!itemId) return;
    setConfirmDelete(null);

    setDeletingItemId(itemId);
    try {
      await api.deleteItem(itemId);
      handleCloseModal();

      // Remove item from the cached items list
      queryClient.setQueryData(itemsKey, (old) => old ? {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          items: page.items.filter(item => (item.item_id || item.id) !== itemId),
          total: Math.max(0, page.total - 1),
        })),
      } : old);

      // Also remove from search results if in search mode
      setSearchResults(prev => prev ? {
        ...prev,
        items: prev.items?.filter(item => (item.item_id || item.id) !== itemId),
        total_items: Math.max(0, (prev.total_items || 0) - 1),
        total_results: Math.max(0, (prev.total_results || 0) - 1),
      } : null);

      // Refresh categories in background to update counts
      queryClient.invalidateQueries({ queryKey: queryKeys.categories(user?.id) });

      showToast('Item deleted', 'success');
    } catch (err) {
      showToast('Failed to delete item', 'error');
    } finally {
      setDeletingItemId(null);
    }
  }, [queryClient, itemsKey, user?.id, showToast, handleCloseModal]);

  // Handle category click from modal - close modal and filter by category
  const handleCategoryClick = useCallback((category, subcategory = null) => {
    setSearchParams((prev) => {
      if (category === 'all') prev.delete('category'); else prev.set('category', category);
      if (subcategory) prev.set('subcategory', subcategory); else prev.delete('subcategory');
      prev.delete('item');
      prev.delete('q');
      return prev;
    }, { replace: true });
    setSearchResults(null);
    setSearchBoxResetKey(k => k + 1);
  }, [setSearchParams]);

  // Handle category selection from sidebar
  const handleSelectCategory = useCallback((categoryId, subcategoryId = null) => {
    setSearchParams((prev) => {
      if (categoryId === 'all') prev.delete('category'); else prev.set('category', categoryId);
      if (subcategoryId) prev.set('subcategory', subcategoryId); else prev.delete('subcategory');
      prev.delete('item');
      prev.delete('q');
      return prev;
    }, { replace: true });
    setSearchResults(null);
    setSearchBoxResetKey(k => k + 1);
  }, [setSearchParams]);

  // handleCloseModal comes from useUrlModalParam above (always a replace —
  // see that hook's doc comment for why).

  // Stable callback for modal refresh
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: itemsKey });
    queryClient.invalidateQueries({ queryKey: queryKeys.categories(user?.id) });
  }, [queryClient, itemsKey, user?.id]);

  // Derive page title based on current state
  const getPageTitle = () => {
    if (searchQuery) return `Search: "${searchQuery}"`;
    if (selectedCategory === 'all') return 'All Items';

    const categoryName = categories.find(c => c.id === selectedCategory)?.name || selectedCategory;
    if (selectedSubcategory) return `${categoryName} / ${selectedSubcategory}`;

    return categoryName;
  };

  const noItemsVisible = sortedItems.length === 0;

  return (
    <div className="app-layout">
      <Sidebar
        categories={categories}
        selectedCategory={selectedCategory}
        selectedSubcategory={selectedSubcategory}
        onSelectCategory={handleSelectCategory}
        user={user}
        onLogout={logout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </button>
            <h1 className="page-title">{getPageTitle()}</h1>
            {searchResults?.answer && (
              <span style={{
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                marginLeft: 'var(--space-md)',
              }}>
                {searchResults.items?.length || 0} results
              </span>
            )}
          </div>

          <div className="header-actions">
            <SearchBox onSearch={handleSearch} defaultValue={searchQuery} key={searchBoxResetKey} />

            {/* Admin: user filter dropdown */}
            {isAdmin && uniqueUsers.length > 1 && (
              <select
                className="sort-select"
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); handleCloseModal(); }}
                title="Filter by user"
              >
                <option value="all">All Users</option>
                {uniqueUsers.map(uid => (
                  <option key={uid} value={uid}>
                    {uid.substring(0, 8)}...
                  </option>
                ))}
              </select>
            )}

            {/* Hide sort dropdown during search - results are sorted by relevance */}
            {!searchResults && (
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            <button
              className="btn btn-ghost"
              onClick={() => isSelectMode ? handleExitSelectMode() : setIsSelectMode(true)}
            >
              {isSelectMode ? 'Cancel' : 'Select'}
            </button>

            <button className="btn btn-secondary" onClick={() => setShowSaveUrl(true)}>
              <LinkSimple size={18} />
              <span>Save URL</span>
            </button>

            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <PlusIcon />
              <span>Upload</span>
            </button>
          </div>
        </header>

        {/* AI Answer (when searching) */}
        <SearchSummary
          answer={searchResults?.answer}
          items={searchResults?.items}
          onItemClick={handleItemClick}
        />

        <div className="content-body">
          {/* Stale-while-refetch (COL-95): a search refetch (`searching`)
              only shows the full skeleton when there's nothing on screen
              yet (first-ever search, or an empty category). Otherwise the
              previously-visible items — browse grid or prior search
              results, `handleSearch` deliberately keeps them in state
              during a refetch — stay up and are just dimmed below, instead
              of the whole grid blanking out. */}
          {(loading || searching) && noItemsVisible ? (
            <CardGridSkeleton />
          ) : noItemsVisible ? (
            <EmptyState
              title={searchQuery ? 'No results found' : 'No items yet'}
              description={searchQuery
                ? 'Try a different search term or clear your filters'
                : 'Upload your first screenshot to get started'
              }
              action={!searchQuery && (
                <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
                  <PlusIcon />
                  Upload Screenshot
                </button>
              )}
            />
          ) : (
            <motion.div
              className="items-grid"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.03 } },
              }}
              aria-busy={searching || undefined}
              style={searching ? { opacity: 0.55, transition: 'opacity 0.15s ease' } : undefined}
            >
              <AnimatePresence mode="sync">
                {sortedItems.map((item, index) => {
                  const itemId = item.item_id || item.id;
                  const jobId = processingJobs[itemId];

                  if (jobId) {
                    return (
                      <ProcessingItemCard
                        key={itemId}
                        item={item}
                        jobId={jobId}
                        index={index}
                        onComplete={() => handleProcessingComplete(itemId)}
                        onFailed={() => handleProcessingFailed(itemId)}
                        onClick={() => handleItemClick(item)}
                      />
                    );
                  }

                  return (
                    <div
                      key={itemId}
                      style={{ position: 'relative', cursor: isSelectMode ? 'pointer' : undefined }}
                      onClick={isSelectMode ? () => handleItemClick(item) : undefined}
                    >
                      {isSelectMode && (
                        <div
                          className={`collection-item-checkbox ${selectedItemIds.has(itemId) ? 'checked' : ''}`}
                          style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, pointerEvents: 'none' }}
                        >
                          {selectedItemIds.has(itemId) && (
                            <Check size={14} />
                          )}
                        </div>
                      )}
                      <ItemCard
                        item={item}
                        onClick={isSelectMode ? undefined : () => handleItemClick(item)}
                        index={index}
                        justCompleted={justCompletedIds.has(itemId)}
                        onRevealed={() => clearJustCompleted(itemId)}
                      />
                    </div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Load more sentinel & loading indicator */}
          {!searchResults && sortedItems.length > 0 && (
            <div
              ref={loadMoreRef}
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 'var(--space-xl)',
                minHeight: '60px',
              }}
            >
              {loadingMore && <div className="loading-spinner" />}
              {!loadingMore && hasMore && (
                <button
                  className="btn btn-secondary"
                  onClick={loadMoreItems}
                  style={{ opacity: 0.7 }}
                >
                  Load More ({items.length} of {totalItems})
                </button>
              )}
            </div>
          )}
        </div>

        <footer className="app-footer">
          <span>&copy; 2026 Collections. All rights reserved.</span>
          <div className="footer-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
          </div>
        </footer>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedItem && (
          <ItemDetailModal
            item={selectedItem}
            onClose={handleCloseModal}
            onDelete={() => handleDeleteRequest(selectedItem.id)}
            onRefresh={handleRefresh}
            onCategoryClick={handleCategoryClick}
            onChatAbout={(item) => navigate('/chat', { state: { itemContext: item } })}
            categoriesData={categoriesData}
            isLoadingDetails={loadingItemId === (selectedItem.item_id || selectedItem.id)}
            isDeleting={deletingItemId === selectedItem.id}
            onPrev={handlePrevItem}
            onNext={handleNextItem}
            hasPrev={selectedItemIndex > 0}
            hasNext={selectedItemIndex < sortedItems.length - 1}
            itemIndex={selectedItemIndex}
            totalItems={sortedItems.length}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpload && (
          <UploadModal
            onClose={() => setShowUpload(false)}
            onUpload={handleUploadComplete}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSaveUrl && (
          <SaveUrlModal
            onClose={() => setShowSaveUrl(false)}
            onCollected={handleCollected}
            onAlreadyCollected={handleAlreadyCollected}
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <Toast toast={toast} />

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            className="confirm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirmDelete(null)}
          >
            <motion.div
              className="confirm-dialog"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="confirm-dialog-title">Delete Item</h3>
              <p className="confirm-dialog-message">
                Are you sure you want to delete this item? This cannot be undone.
              </p>
              <div className="confirm-dialog-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setConfirmDelete(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteConfirm(confirmDelete)}
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selection Bar for multiselect mode */}
      <AnimatePresence>
        {isSelectMode && selectedItemIds.size > 0 && (
          <SelectionBar
            count={selectedItemIds.size}
            onAddToCollection={() => setShowCollectionPicker(true)}
            onCancel={handleExitSelectMode}
          />
        )}
      </AnimatePresence>

      {/* Collection Picker Modal */}
      <AnimatePresence>
        {showCollectionPicker && (
          <CollectionPickerModal
            items={Array.from(selectedItemIds).map(id => ({ item_id: id, item_type: 'item' }))}
            onClose={() => setShowCollectionPicker(false)}
            onSuccess={handleCollectionPickerSuccess}
          />
        )}
      </AnimatePresence>

    </div>
  );
}

// Wrapper component for items being processed - uses the polling hook
function ProcessingItemCard({ item, jobId, index, onComplete, onFailed, onClick }) {
  const { progress, currentPhase, isComplete, isFailed, isTimedOut, retry } = useProcessingStatus(jobId, {
    pollInterval: 2000,
    onComplete,
    onError: onFailed,
  });

  // Once complete or failed, the parent will re-render without this wrapper
  // But we still render the card to avoid flicker
  return (
    <ItemCard
      item={item}
      onClick={onClick}
      index={index}
      processingState={{
        isProcessing: !isComplete && !isFailed,
        progress,
        currentPhase,
        isTimedOut,
        onRetry: retry,
      }}
    />
  );
}

// Icons
function MenuIcon() {
  return <List size={20} />;
}

function PlusIcon() {
  return <Plus size={18} />;
}
